import { useState, useEffect, useRef, KeyboardEvent, useCallback, ChangeEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Message, MessageStatus } from '../services/api'
import { useSignalProtocol } from '../hooks/useSignalProtocol'
import { WSMessageTypes } from '../hooks/useWebSocket'
import { useSharedWebSocket } from '../contexts/WebSocketContext'
import ChatBubble from '../components/ChatBubble'

const MAX_FILE_SIZE = 50 * 1024 * 1024
const EMOJIS = ['😀','😂','❤️','👍','👎','🔥','🎉','😢','😮','🙏','💯','🤔','😡','🥰','😎','👋']

export default function ChatPage() {
  const { recipientId } = useParams<{ recipientId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [recipientName] = useState(recipientId || '')
  const [isTyping, setIsTyping] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<any>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<any>(null)

  const { encrypt, decryptAsync } = useSignalProtocol(recipientId || '', user?.id)
  const { lastMessage, isConnected, sendTyping, sendReadReceipt } = useSharedWebSocket()

  useEffect(() => { if (recipientId) loadMessages() }, [recipientId])

  const decryptAsyncRef = useRef(decryptAsync)
  useEffect(() => { decryptAsyncRef.current = decryptAsync }, [decryptAsync])

  // Polling fallback
  useEffect(() => {
    if (isConnected || !recipientId) return
    let delay = 3000; let timeoutId: ReturnType<typeof setTimeout>; let cancelled = false
    const poll = async () => {
      if (cancelled) return
      try {
        const data = await api.getMessages(recipientId)
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id))
          const uniqueNew = data.filter((m: Message) => !existingIds.has(m.id))
          if (uniqueNew.length > 0) {
            delay = 3000
            Promise.all(uniqueNew.map(async (msg: Message) => {
              if (msg.ciphertext) {
                try { return { ...msg, text: await decryptAsyncRef.current(msg.ciphertext) } }
                catch { return { ...msg, text: '[Unable to decrypt]' } }
              }
              return msg
            })).then(decrypted => {
              if (!cancelled) setMessages(p => [...p.filter(m => !decrypted.some(d => d.id === m.id)), ...decrypted].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()))
            })
            return [...prev, ...uniqueNew]
          }
          delay = Math.min(delay * 1.5, 30000); return prev
        })
      } catch { delay = Math.min(delay * 2, 30000) }
      if (!cancelled) timeoutId = setTimeout(poll, delay)
    }
    timeoutId = setTimeout(poll, delay)
    return () => { cancelled = true; clearTimeout(timeoutId) }
  }, [isConnected, recipientId])

  useEffect(() => {
    if (isRecording) { timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000) }
    else { if (timerRef.current) clearInterval(timerRef.current); setRecordingTime(0) }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [isRecording])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!lastMessage) return
    const handleMsg = async () => {
      if (lastMessage.type === WSMessageTypes.MESSAGE || lastMessage.type === 'new_message') {
        const p = lastMessage.payload as any
        const sid = p.sender_id || p.senderId; const rid = p.recipient_id || p.recipientId
        if (sid !== recipientId && rid !== recipientId) { if (sid && sid !== user?.id) showNotification(sid); return }
        const mid = p.id || `ws-${Date.now()}`
        setMessages(prev => {
          if (prev.some(m => m.id === mid)) return prev
          const ct = p.ciphertext || p.text || ''
          const nm: Message = { id: mid, senderId: sid || '', text: '[解密中...]', timestamp: p.timestamp || new Date().toISOString(), status: 'delivered', ciphertext: ct, reply_to: p.reply_to }
          decryptAsync(ct).then(t => setMessages(c => c.map(m => m.id === mid ? { ...m, text: t } : m))).catch(() => setMessages(c => c.map(m => m.id === mid ? { ...m, text: '[無法解密]' } : m)))
          if (sid === recipientId && recipientId) sendReadReceipt(recipientId, [mid])
          return [...prev, nm]
        })
      }
      if (lastMessage.type === WSMessageTypes.TYPING) {
        const p = lastMessage.payload as any
        if ((p.sender_id || p.from_id) === recipientId) { setIsTyping(true); setTimeout(() => setIsTyping(false), 3000) }
      }
      if (lastMessage.type === WSMessageTypes.READ) {
        const p = lastMessage.payload as any
        if (p.from_id === recipientId && p.message_ids) setMessages(prev => prev.map(m => p.message_ids.includes(m.id) ? { ...m, status: 'read' as MessageStatus } : m))
      }
      if (lastMessage.type === 'message_deleted') {
        const p = lastMessage.payload as any
        if (p.message_id) setMessages(prev => prev.map(m => m.id === p.message_id ? { ...m, deleted: true } : m))
      }
      if (lastMessage.type === 'reaction') {
        const p = lastMessage.payload as any
        if (p.message_id && p.emoji) {
          setMessages(prev => prev.map(m => {
            if (m.id !== p.message_id) return m
            const reactions = { ...(m.reactions || {}) }
            if (!reactions[p.emoji]) reactions[p.emoji] = []
            if (!reactions[p.emoji].includes(p.user_id || '')) reactions[p.emoji] = [...reactions[p.emoji], p.user_id || '']
            return { ...m, reactions }
          }))
        }
      }
    }
    handleMsg()
  }, [lastMessage, recipientId, user?.id, decryptAsync, sendReadReceipt])

  useEffect(() => { if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission() }, [])

  const showNotification = useCallback((senderId: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const n = new Notification('LYNKSTAR - 新訊息', { body: `來自 ${senderId.substring(0, 8)}...`, icon: '/favicon.ico', tag: `msg-${senderId}` })
      n.onclick = () => { window.focus(); navigate(`/chat/${senderId}`); n.close() }
      setTimeout(() => n.close(), 5000)
    }
  }, [navigate])

  const loadMessages = async () => {
    try {
      setIsLoading(true)
      const data = await api.getMessages(recipientId!)
      const myUUID = localStorage.getItem('lynkstar_my_uuid')
      let foundMyUUID: string | undefined; let foundTheirUUID: string | undefined
      for (const msg of data) {
        if (!msg.senderId) continue
        if (msg.senderId !== myUUID && !foundTheirUUID) foundTheirUUID = msg.senderId
        if (!foundMyUUID && msg.senderId !== foundTheirUUID) foundMyUUID = msg.senderId
      }
      if (foundMyUUID) localStorage.setItem('lynkstar_my_uuid', foundMyUUID)
      if (foundTheirUUID && foundTheirUUID !== recipientId) localStorage.setItem(`lynkstar_uuid_${recipientId}`, foundTheirUUID)

      const decryptedMessages = await Promise.all(data.map(async (msg) => {
        if (msg.text && msg.text.startsWith('PLAIN:')) { try { return { ...msg, text: await decryptAsync(msg.text) } } catch { return msg } }
        if (msg.ciphertext) { try { return { ...msg, text: await decryptAsync(msg.ciphertext) } } catch { return { ...msg, text: '[Unable to decrypt]' } } }
        return msg
      }))
      setMessages(decryptedMessages); setHasMore(data.length >= 50)
      const unread = data.filter(m => m.senderId === recipientId && m.status !== 'read').map(m => m.id)
      if (unread.length > 0 && recipientId) { sendReadReceipt(recipientId, unread); api.markAsRead(recipientId, unread).catch(() => {}) }
    } catch (error) { console.error('Failed to load messages:', error) }
    finally { setIsLoading(false) }
  }

  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMore || messages.length === 0) return
    setIsLoadingMore(true)
    try {
      const data = await api.getMessages(recipientId!, 50, messages[0].timestamp)
      if (data.length === 0) { setHasMore(false); return }
      const dec = await Promise.all(data.map(async (msg) => { if (msg.ciphertext) { try { return { ...msg, text: await decryptAsync(msg.ciphertext) } } catch { return { ...msg, text: '[Unable to decrypt]' } } } return msg }))
      setMessages(prev => [...dec, ...prev]); setHasMore(data.length >= 50)
    } catch (e) { console.error('Failed to load more:', e) }
    finally { setIsLoadingMore(false) }
  }

  const handleScroll = () => {
    if (messagesContainerRef.current && messagesContainerRef.current.scrollTop < 50 && hasMore && !isLoadingMore) loadMoreMessages()
  }

  const handleSendMessage = async () => {
    const text = newMessage.trim(); if (!text || !recipientId) return
    const opt: Message = { id: `temp-${Date.now()}`, senderId: user?.id || 'me', text, timestamp: new Date().toISOString(), status: 'sending', reply_to: replyingTo?.id }
    setMessages(prev => [...prev, opt]); setNewMessage(''); setReplyingTo(null); inputRef.current?.focus()
    try {
      const ct = await encrypt(text); const sent = await api.sendMessage(recipientId, ct, replyingTo?.id)
      if (sent.senderId && sent.senderId !== recipientId && !localStorage.getItem('lynkstar_my_uuid')) localStorage.setItem('lynkstar_my_uuid', sent.senderId)
      setMessages(prev => prev.map(msg => msg.id === opt.id ? { ...sent, text, status: 'sent' as MessageStatus, reply_to: opt.reply_to } : msg))
    } catch (error) {
      console.error('Failed to send:', error)
      setMessages(prev => prev.map(msg => msg.id === opt.id ? { ...msg, status: 'failed' as MessageStatus } : msg))
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage() } }

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value)
    if (recipientId && isConnected) { if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current); sendTyping(recipientId); typingTimeoutRef.current = setTimeout(() => {}, 3000) }
  }

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files; if (!files || files.length === 0 || !recipientId) return
    const file = files[0]; if (file.size > MAX_FILE_SIZE) { setUploadError(`文件過大 (最大 ${MAX_FILE_SIZE / 1024 / 1024}MB)`); return }
    setShowAttachmentMenu(false); setUploadError(null); setUploadProgress(0)
    const isImage = file.type.startsWith('image/'); const isVideo = file.type.startsWith('video/'); const isAudio = file.type.startsWith('audio/')
    const opt: Message = { id: `temp-${Date.now()}`, senderId: user?.id || 'me', text: isImage ? '[圖片]' : isVideo ? '[影片]' : isAudio ? '[語音]' : '[文件]', timestamp: new Date().toISOString(), status: 'sending' }
    setMessages(prev => [...prev, opt])
    try {
      const res = await api.uploadFile(file, recipientId); setUploadProgress(50)
      const mt = isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : 'file'
      const mc = JSON.stringify({ type: mt, url: res.url, id: res.id, name: file.name, size: file.size })
      const ct = await encrypt(`[MEDIA]${mc}`); const sent = await api.sendMessage(recipientId, ct); setUploadProgress(100)
      setMessages(prev => prev.map(msg => msg.id === opt.id ? { ...sent, text: `[MEDIA]${mc}`, status: 'sent' as MessageStatus } : msg))
    } catch (err) {
      console.error('Upload failed:', err); setUploadError('上傳失敗')
      setMessages(prev => prev.map(m => m.id === opt.id ? { ...m, status: 'failed' } : m))
    } finally { setUploadProgress(null); if (fileInputRef.current) fileInputRef.current.value = ''; if (cameraInputRef.current) cameraInputRef.current.value = '' }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream); mediaRecorderRef.current = mr; audioChunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data) }
      mr.onstop = async () => { const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' }); stream.getTracks().forEach(t => t.stop()); await handleVoiceUpload(new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' })) }
      mr.start(); setIsRecording(true)
    } catch { alert('無法訪問麥克風') }
  }

  const stopRecording = () => { if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false) } }

  const handleVoiceUpload = async (file: File) => {
    if (!recipientId) return
    const opt: Message = { id: `temp-${Date.now()}`, senderId: user?.id || 'me', text: '[語音訊息]', timestamp: new Date().toISOString(), status: 'sending' }
    setMessages(prev => [...prev, opt])
    try {
      const res = await api.uploadFile(file, recipientId)
      const mc = JSON.stringify({ type: 'audio', url: res.url, id: res.id, name: file.name, size: file.size })
      const ct = await encrypt(`[MEDIA]${mc}`); const sent = await api.sendMessage(recipientId, ct)
      setMessages(prev => prev.map(m => m.id === opt.id ? { ...sent, text: `[MEDIA]${mc}`, status: 'sent' as MessageStatus } : m))
    } catch { setMessages(prev => prev.map(m => m.id === opt.id ? { ...m, status: 'failed' } : m)) }
  }

  const handleReply = (message: Message) => { setReplyingTo(message); inputRef.current?.focus() }
  const handleReaction = async (messageId: string, emoji: string) => {
    try { await api.addReaction(messageId, emoji)
      setMessages(prev => prev.map(m => { if (m.id !== messageId) return m; const r = { ...(m.reactions || {}) }; if (!r[emoji]) r[emoji] = []; const uid = user?.id || 'me'; if (r[emoji].includes(uid)) { r[emoji] = r[emoji].filter(id => id !== uid); if (!r[emoji].length) delete r[emoji] } else r[emoji] = [...r[emoji], uid]; return { ...m, reactions: r } }))
    } catch {}
  }
  const handleDeleteMessage = async (messageId: string) => { try { await api.deleteMessage(messageId); setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deleted: true } : m)) } catch {} }

  const insertEmoji = (emoji: string) => { setNewMessage(prev => prev + emoji); setShowEmojiPicker(false); inputRef.current?.focus() }

  const formatMessageTime = (timestamp: string) => {
    const d = new Date(timestamp); const now = new Date()
    const time = d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })
    if (d.toDateString() === now.toDateString()) return time
    const y = new Date(now); y.setDate(y.getDate() - 1)
    if (d.toDateString() === y.toDateString()) return `昨天 ${time}`
    return d.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' }) + ' ' + time
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* App Bar */}
      <header className="app-bar sticky top-0 z-10">
        <button onClick={() => navigate('/chats')} className="p-1 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
        <div className="avatar avatar-sm bg-primary-light text-primary">{recipientName.charAt(0).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{recipientName}</h1>
          <div className="flex items-center gap-1">
            {isTyping ? <span className="text-xs text-primary animate-pulse">正在輸入...</span> : (
              <><svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-secure-green" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg><span className="text-xs text-secure-green">端對端加密</span>{isConnected && <span className="text-xs text-green-500 ml-1">(即時)</span>}</>
            )}
          </div>
        </div>
        <button onClick={() => navigate(`/call/${recipientId}?type=video`)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></button>
        <button onClick={() => navigate(`/call/${recipientId}?type=audio`)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg></button>
        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg></button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>驗證身份</button>
              <button className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>清除聊天</button>
              <button className="w-full px-4 py-3 text-left text-error hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>封鎖用戶</button>
            </div>
          )}
        </div>
      </header>

      {/* Messages */}
      <main ref={messagesContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4">
        {isLoadingMore && <div className="flex justify-center py-2"><div className="spinner border-primary border-t-primary-light" /></div>}
        {hasMore && !isLoadingMore && messages.length > 0 && <button onClick={loadMoreMessages} className="w-full text-center text-sm text-primary py-2 hover:underline">載入更多</button>}
        {isLoading ? (
          <div className="flex items-center justify-center h-full"><div className="spinner border-primary border-t-primary-light" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-secure-green/10 rounded-full flex items-center justify-center mb-4"><svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg></div>
            <p className="text-secure-green text-sm font-medium mb-2">端對端加密保護</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">只有你和 {recipientName} 可以閱讀訊息</p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => {
              const isReceived = message.senderId === recipientId
              return (
                <ChatBubble
                  key={message.id}
                  message={message}
                  isSent={!isReceived}
                  formatTime={formatMessageTime}
                  onReply={handleReply}
                  onReaction={handleReaction}
                  onDelete={handleDeleteMessage}
                  replyMessage={messages.find(m => m.id === message.reply_to) || null}
                />
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Reply Preview */}
      {replyingTo && (
        <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 px-4 py-2 flex items-center gap-3">
          <div className="w-1 h-10 bg-primary rounded-full" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary">回覆 {replyingTo.senderId === recipientId ? recipientName : '你'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{replyingTo.text?.startsWith('[MEDIA]') ? '📎 媒體訊息' : replyingTo.text}</p>
          </div>
          <button onClick={() => setReplyingTo(null)} className="p-1 text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 px-4 py-3 sticky bottom-0">
        <div className="flex items-end gap-2">
          {/* Emoji Button */}
          <div className="relative">
            <button onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowAttachmentMenu(false) }} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-3 animate-fade-in">
                <div className="grid grid-cols-8 gap-1">{EMOJIS.map(e => <button key={e} onClick={() => insertEmoji(e)} className="text-xl p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">{e}</button>)}</div>
              </div>
            )}
          </div>

          {/* Attachment Button */}
          <div className="relative">
            <button onClick={() => { setShowAttachmentMenu(!showAttachmentMenu); setShowEmojiPicker(false) }} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            </button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />
            <input ref={cameraInputRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={handleFileSelect} />
            {showAttachmentMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-4 animate-fade-in w-72">
                <div className="grid grid-cols-4 gap-4">
                  <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.setAttribute('accept', 'image/*,video/*'); fileInputRef.current.click() } }} className="flex flex-col items-center gap-2"><div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center hover:bg-purple-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div><span className="text-xs text-gray-600 dark:text-gray-400">相簿</span></button>
                  <button onClick={() => cameraInputRef.current?.click()} className="flex flex-col items-center gap-2"><div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center hover:bg-pink-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div><span className="text-xs text-gray-600 dark:text-gray-400">相機</span></button>
                  <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.setAttribute('accept', 'audio/*'); fileInputRef.current.click() } }} className="flex flex-col items-center gap-2"><div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg></div><span className="text-xs text-gray-600 dark:text-gray-400">音樂</span></button>
                  <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.removeAttribute('accept'); fileInputRef.current.click() } }} className="flex flex-col items-center gap-2"><div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center hover:bg-orange-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 011.414.586l5.414 5.414a1 1 0 01.586 1.414V19a2 2 0 01-2 2z" /></svg></div><span className="text-xs text-gray-600 dark:text-gray-400">文件</span></button>
                </div>
              </div>
            )}
            {uploadProgress !== null && <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-3 animate-fade-in"><div className="flex items-center gap-2"><div className="spinner border-primary border-t-primary-light w-4 h-4" /><span className="text-sm text-gray-600 dark:text-gray-400">上傳中... {uploadProgress}%</span></div></div>}
            {uploadError && <div className="absolute bottom-full left-0 mb-2 bg-red-50 dark:bg-red-900/20 rounded-xl shadow-lg p-3 animate-fade-in"><div className="flex items-center gap-2 text-red-600 dark:text-red-400"><span className="text-sm">{uploadError}</span><button onClick={() => setUploadError(null)}><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div></div>}
          </div>

          {/* Text Input */}
          <div className="flex-1">
            <textarea ref={inputRef} value={newMessage} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="輸入訊息..." rows={1} className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-3xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 dark:text-white placeholder:text-gray-400" style={{ maxHeight: '120px' }} />
          </div>

          {/* Send / Record */}
          {newMessage.trim() ? (
            <button onClick={handleSendMessage} className="p-3 bg-primary text-white rounded-full hover:bg-primary-dark disabled:opacity-50"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg></button>
          ) : (
            <button className={`p-3 rounded-full transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse scale-110 shadow-lg' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`} onMouseDown={startRecording} onMouseUp={stopRecording} onTouchStart={startRecording} onTouchEnd={stopRecording} title="按住錄音">
              {isRecording ? <span className="font-mono text-xs font-bold">{Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}</span> : <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>}
            </button>
          )}
        </div>
      </div>

      {/* Click outside to close menus */}
      {(showMenu || showAttachmentMenu || showEmojiPicker) && <div className="fixed inset-0 z-40" onClick={() => { setShowMenu(false); setShowAttachmentMenu(false); setShowEmojiPicker(false) }} />}
    </div>
  )
}